import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TagInput } from "./TagInput";

interface MediaEditDialogProps {
  media: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const MediaEditDialog = ({ media, open, onOpenChange, onSuccess }: MediaEditDialogProps) => {
  const [title, setTitle] = useState(media?.title || '');
  const [altText, setAltText] = useState(media?.alt_text || '');
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    if (media) {
      setTitle(media.title || '');
      setAltText(media.alt_text || '');
      // Extract tags if available
      const mediaTags = media.media_tag_relations?.map((rel: any) => rel.media_tags.name) || [];
      setTags(mediaTags);
    }
  }, [media]);

  const imageUrl = media ? supabase.storage
    .from('media-library')
    .getPublicUrl(media.storage_path).data.publicUrl : '';

  const handleSave = async () => {
    if (!media) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('media_library')
        .update({
          title,
          alt_text: altText,
        })
        .eq('id', media.id);

      if (error) throw error;

      toast.success('Media updated successfully');
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating media:', error);
      toast.error('Failed to update media');
    } finally {
      setSaving(false);
    }
  };

  if (!media) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Media Details</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="aspect-video relative bg-muted rounded-lg overflow-hidden">
            <img
              src={imageUrl}
              alt={media.filename}
              className="w-full h-full object-contain"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="filename">Filename</Label>
            <Input
              id="filename"
              value={media.filename}
              disabled
              className="bg-muted"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="title">Title (Optional)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title for this image"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="alt-text">Alt Text</Label>
            <Textarea
              id="alt-text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Describe this image for accessibility"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Alt text helps with accessibility and SEO
            </p>
          </div>

          <div className="grid gap-2">
            <Label>Tags</Label>
            <TagInput
              mediaId={media.id}
              selectedTags={tags}
              onTagsChange={setTags}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Size:</span>{' '}
              {(media.file_size / 1024 / 1024).toFixed(2)} MB
            </div>
            <div>
              <span className="text-muted-foreground">Dimensions:</span>{' '}
              {media.width} × {media.height}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
